import styles from "./DeviceTable.module.scss";
import info from "../../assets/icons/info.svg";
import infoHover from "../../assets/icons/info-hover.svg";
import tooltip from "../../assets/icons/tooltip.svg";
import Modal from "../modal/modal";
import { useEffect, useMemo, useState } from "react";
import DeactivateDeviceModal from "../modals/DeactiveDeviceModal/DeactiveDeviceModal";
import { useTranslation } from "react-i18next";
import Button from "../button/Button";
import GetPremiumModal from "../modals/GetPremiumModal/GetPremiumModal";
import OrderSummaryModal from "../modals/OrderSummaryModal/OrderSummaryModal";
import RenewalModal from "../modals/RenewalModal/RenewalModal";
import ProcessingModal from "../modals/ProcessingModal/ProcessingModal";
import SuccessfullyRenewalModal from "../modals/SuccessfullyRenewalModal/SuccessfullyRenewalModal";

import { deviceProps, ILicenses, subscriptionData } from "../../types/details-api-types";
import { useToast } from "../../hooks/useToast";
import DeviceItem from "./DeviceItem";

interface ExtendedSubscriptionData extends subscriptionData {
  licenses: ILicenses[];
}
interface DeviceTableProps {
  subNumber: string;
  title?: string;
  number: string;
  data: any;
  expired?: boolean;
  trial?: boolean;
  pause?: boolean;
  canceled?: boolean;
  setIsModalManageSubVisible?: any;
  updateDevicesTable?: () => void;
  isLoadingDeactivate?: boolean;
  setIsLoadingDeactivate?: (arg0: boolean) => void;
  setDeactivateSuccess: (arg: boolean) => void;
  subscriptions: ExtendedSubscriptionData;
  refreshData: () => void;
}

const DeviceTable: React.FC<DeviceTableProps> = ({
  subNumber,
  number,
  data,
  title,
  pause,
  expired,
  trial,
  canceled,
  updateDevicesTable,
  isLoadingDeactivate,
  setIsLoadingDeactivate,
  setDeactivateSuccess,
  subscriptions,
  refreshData,
}) => {
  const [isModalDeactivateVisible, setIsModalDeactivateVisible] = useState(false);
  const [isModalUpdateVisible, setIsModalUpdateVisible] = useState(false);
  const [isModalOrderVisible, setIsModalOrderVisible] = useState(false);
  const [isModalRenewalVisible, setIsModalRenewalVisible] = useState(false);
  const [isModalSuccessfulVisible, setIsModalSuccessfulVisible] = useState(false);
  const [isModalProcessingVisible, setIsModalProcessingVisible] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [currentDeviceDeactivate, setCurrentDeviceDeactivate] = useState<deviceProps | null>(null);

  const { showToast } = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    if (!isLoadingDeactivate) {
      setIsModalDeactivateVisible(false);
    }
  }, [isLoadingDeactivate]);

  const mergedData = useMemo(() => {
    if (!data || !subscriptions) return { matches: 0, devices: [] };

    let matches = 0;

    const newData = data.map((device: { machine_id: any }) => {
      const matchingLicenses = subscriptions.licenses.filter((license) => license.machine_id === device.machine_id);

      // Get the first matching license_key or set to an empty string if no match
      const licenseKey = matchingLicenses.length > 0 ? matchingLicenses[0].license_key : "";

      if (matchingLicenses.length > 0) ++matches;

      return {
        ...device,
        license_key: licenseKey,
        expiration_date: matchingLicenses.length > 0 ? matchingLicenses[0]?.expiration_date : "",
        matchingLicenses,
        hasMatch: matchingLicenses.length > 0,
      };
    });

    return {
      devices: newData,
      matches,
    };
  }, [data, subscriptions]);

  const subscriptionExpiration = useMemo(() => {
    const expirationDate = new Date(subscriptions?.licenses?.[0]?.expiration_date || "").getTime();
    const today = new Date().getTime();

    // Calculate days left
    const timeDiff = expirationDate - today;
    const daysLeft = Math.max(Math.ceil(timeDiff / (1000 * 60 * 60 * 24)), 0);

    return {
      isExpired: timeDiff < 0,
      daysLeft,
    };
  }, [subscriptions]);

  const isPackageExpired = expired || subscriptionExpiration.isExpired;
  return (
    <div className={styles.root}>
      <div className={styles.root_wrapper}>
        {isPackageExpired ? (
          <p className={styles.root_expired}>{t("Package expired")}</p>
        ) : trial ? (
          <p className={styles.root_pause}>Expires in: Soon</p>
        ) : pause ? (
          <p className={styles.root_pause}>{t("Paused")}</p>
        ) : canceled ? (
          <p className={styles.root_expired}>
            {t("Canceled")} - {t("Expires in {{num}} days", { num: subscriptionExpiration.daysLeft })}
          </p>
        ) : null}

        <div className={styles.root_header}>
          <div className={styles.root_header_title}>
            <p className={styles.root_header_text}>{title}</p>
            {!isPackageExpired && !trial && <p className={styles.root_header_count}>{number}</p>}
          </div>

          {isPackageExpired ? (
            <Button
              text={t("Renew now")}
              onClick={() => setIsModalRenewalVisible(true)}
              classNames={styles.root_header_button}
            />
          ) : trial ? (
            <Button
              onClick={() => setIsModalUpdateVisible(true)}
              classNames={styles.root_header_button}
              type="expired"
              text={t("Upgrade now")}
            />
          ) : pause ? (
            <Button
              text={t("Renew now")}
              onClick={() => setIsModalRenewalVisible(true)}
              classNames={styles.root_header_button}
            />
          ) : canceled ? (
            <Button
              text={t("Renew now")}
              onClick={() => window.open("https://secure.fortect.com/", "_blank")}
              classNames={styles.root_header_button}
            />
          ) : null}
        </div>
      </div>
      {isPackageExpired && (
        <p className={styles.root_subExpired}>
          {t("Subscription #:")} {subNumber}
        </p>
      )}

      {pause && (
        <p className={styles.root_subExpired}>
          {t("Subscription #:")} {subNumber}
        </p>
      )}

      {canceled && (
        <p className={styles.root_subExpired}>
          {t("Subscription #:")} {subNumber}
        </p>
      )}

      <div className={styles.root_property}>
        <div>
          <p>{t("Device Name")}</p>
        </div>
        <div>
          <p>{t("Operating System")}</p>
        </div>
        <div>
          <p>{t("Installer download")}</p>
        </div>
        {mergedData.matches > 0 && <div>{t("License Key")}</div>}

        {!trial && (
          <div className={showTooltip ? styles.root_relative : ""}>
            <p>{t("Device Deactivation")}</p>
            <img
              src={info}
              alt="info"
              onMouseOver={(e) => {
                e.currentTarget.src = infoHover;
                setShowTooltip(true);
              }}
              onMouseOut={(e) => {
                e.currentTarget.src = info;
                setShowTooltip(false);
              }}
              className={styles.root_property_info}
            />
            {showTooltip && (
              <div className={styles.root_tooltipWrapper}>
                <img className={styles.root_tooltipWrapper_icon} src={tooltip} alt="tooltip" />
                <p>
                  {t(
                    "Deactivate Device - This feature allows you to deactivate Fortect on this device, freeing up a license slot to use on another device."
                  )}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {isPackageExpired ? (
        <div className={styles.root_noDevices}>{t("No active devices.")}</div>
      ) : subNumber === "0" ? (
        <div className={styles.root_noDevices}>{t("No active devices.")}</div>
      ) : pause ? (
        <div className={styles.root_noDevices}>{t("No active devices.")}</div>
      ) : (
        mergedData.devices.length > 0 &&
        mergedData.devices.map((item: any, index: number) => (
          <DeviceItem
            key={index}
            item={item}
            trial={trial}
            setCurrentDeviceDeactivate={setCurrentDeviceDeactivate}
            setIsModalDeactivateVisible={setIsModalDeactivateVisible}
            showToast={showToast}
            itemKey={item?.license_key}
            anyMatchKey={mergedData.matches > 0}
          />
        ))
      )}

      <Modal isOpen={isModalDeactivateVisible} setIsVisible={() => setIsModalDeactivateVisible(false)}>
        <DeactivateDeviceModal
          setIsModalDeactivateVisible={setIsModalDeactivateVisible}
          setDeactivateDeviceItem={setCurrentDeviceDeactivate}
          setDeactivateSuccess={setDeactivateSuccess}
          deviceItem={currentDeviceDeactivate}
          updateDevicesTable={updateDevicesTable}
          isLoadingDeactivate={isLoadingDeactivate}
          setIsLoadingDeactivate={setIsLoadingDeactivate}
        />
      </Modal>

      <Modal isOpen={isModalUpdateVisible} setIsVisible={() => setIsModalUpdateVisible(false)}>
        <GetPremiumModal
          setIsModalUpdateVisible={setIsModalUpdateVisible}
          setIsModalOrderVisible={setIsModalOrderVisible}
        />
      </Modal>

      <Modal isOpen={isModalOrderVisible} setIsVisible={() => setIsModalOrderVisible(false)}>
        <OrderSummaryModal />
      </Modal>

      <Modal isOpen={isModalRenewalVisible} setIsVisible={() => setIsModalRenewalVisible(false)}>
        <RenewalModal
          setIsModalPauseConfirmVisible={setIsModalRenewalVisible}
          setIsModalProcessingVisible={setIsModalProcessingVisible}
          setIsModalSuccessfullyVisible={setIsModalSuccessfulVisible}
          data={subscriptions}
        />
      </Modal>

      <Modal isOpen={isModalProcessingVisible} setIsVisible={() => setIsModalProcessingVisible(false)}>
        <ProcessingModal setIsModalProcessingVisible={setIsModalProcessingVisible} />
      </Modal>

      <Modal isOpen={isModalSuccessfulVisible} setIsVisible={() => setIsModalSuccessfulVisible(false)}>
        <SuccessfullyRenewalModal
          setIsModalPauseConfirmVisible={() => setIsModalSuccessfulVisible(false)}
          data={subscriptions}
          refreshData={refreshData}
        />
      </Modal>
    </div>
  );
};

export default DeviceTable;
